import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, getDocs, where, orderBy, limit, addDoc, serverTimestamp, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface User {
  uid: string;
  username: string;
  displayName: string;
  email: string;
}

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  receiverId: string;
  text: string;
  timestamp: any;
  isRead: boolean;
  matchData?: any;
}

interface Conversation {
  conversationId: string;
  participantIds: string[];
  participantNames: string[];
  partnerId: string; // The other person in the conversation (not the selected user)
  partnerName: string; // Display name of the partner
  lastMessage: string;
  lastMessageTime: any;
  messageCount: number;
  isAdminChat: boolean;
}

const AdminChat: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedConversationIsAdmin, setSelectedConversationIsAdmin] = useState<boolean>(false);
  const [selectedConversationPartnerId, setSelectedConversationPartnerId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'messages' | 'adminChat'>('messages');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = users.filter(u =>
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Sort filtered results alphabetically by displayName
      setFilteredUsers(filtered.sort((a, b) =>
        (a.displayName || a.username).localeCompare(b.displayName || b.username)
      ));
    } else {
      // Show all users alphabetically when no search term
      setFilteredUsers([...users].sort((a, b) =>
        (a.displayName || a.username).localeCompare(b.displayName || b.username)
      ));
    }
  }, [searchTerm, users]);

  useEffect(() => {
    if (selectedUser) {
      loadConversations(selectedUser.uid);
      setSelectedConversationId(null);
      setMessages([]);
    }
  }, [selectedUser]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        username: doc.data().username || '',
        displayName: doc.data().displayName || '',
        email: doc.data().email || ''
      }));
      // Sort users alphabetically by displayName
      const sortedUsers = usersData.sort((a, b) =>
        (a.displayName || a.username).localeCompare(b.displayName || b.username)
      );
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async (userId: string) => {
    setLoadingMessages(true);
    setMessages([]);
    try {

      const messagesRef = collection(db, 'messages');
      const conversationsRef = collection(db, 'conversations');

      // Get messages where user is sender
      const q1 = query(
        messagesRef,
        where('senderId', '==', userId),
        orderBy('timestamp', 'desc')
      );

      // Get messages where user is receiver
      const q2 = query(
        messagesRef,
        where('receiverId', '==', userId),
        orderBy('timestamp', 'desc')
      );

      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);

      // Fetch conversation documents to get participantIds
      const conversationDocsMap = new Map<string, any>();
      const uniqueConversationIds = new Set<string>();

      sentSnapshot.docs.forEach(doc => {
        if (doc.data().conversationId) {
          uniqueConversationIds.add(doc.data().conversationId);
        }
      });
      receivedSnapshot.docs.forEach(doc => {
        if (doc.data().conversationId) {
          uniqueConversationIds.add(doc.data().conversationId);
        }
      });

      // Fetch all conversation documents
      for (const convId of Array.from(uniqueConversationIds)) {
        try {
          const convDoc = await getDoc(doc(db, 'conversations', convId));
          if (convDoc.exists()) {
            conversationDocsMap.set(convId, convDoc.data());
          }
        } catch (err) {
          console.warn('Could not fetch conversation:', convId, err);
        }
      }

      // Group messages by participants (not conversationId) for admin chats
      // This ensures all admin messages with the same user appear as one conversation
      const conversationMap = new Map<string, {
        conversationIds: string[];
        lastMessage: string;
        lastMessageTime: any;
        messages: any[];
        participantIds: Set<string>;
        hasAdminMessage: boolean;
      }>();

      const processMessage = (data: any) => {
        if (!data.conversationId) {
          console.warn('Message without conversationId');
          return;
        }

        const isAdminMsg = data.isAdminMessage || false;
        let participantKey: string;

        // Get participants from conversation document if available
        const convDoc = conversationDocsMap.get(data.conversationId);
        const convParticipants = convDoc?.participantIds || [];

        if (isAdminMsg) {
          // Admin messages: group by participants (all admin messages together)
          const participants = convParticipants.length > 0
            ? convParticipants.sort()
            : [data.senderId, data.receiverId].filter(Boolean).sort();
          participantKey = participants.join('_') + '_admin';
        } else {
          // Normal messages: group by conversationId (each conversation separate)
          participantKey = data.conversationId + '_normal';
        }

        if (!conversationMap.has(participantKey)) {
          const participants = convParticipants.length > 0
            ? convParticipants
            : [data.senderId, data.receiverId].filter(Boolean);
          conversationMap.set(participantKey, {
            conversationIds: [data.conversationId],
            lastMessage: data.text || '',
            lastMessageTime: data.timestamp,
            messages: [data],
            participantIds: new Set(participants),
            hasAdminMessage: isAdminMsg
          });
        } else {
          const conv = conversationMap.get(participantKey)!;
          if (!conv.conversationIds.includes(data.conversationId)) {
            conv.conversationIds.push(data.conversationId);
          }
          conv.messages.push(data);
          // Add participants from conversation document
          if (convParticipants.length > 0) {
            convParticipants.forEach((p: string) => conv.participantIds.add(p));
          } else {
            if (data.senderId) conv.participantIds.add(data.senderId);
            if (data.receiverId) conv.participantIds.add(data.receiverId);
          }
          // Update last message if this one is newer
          if (data.timestamp && (!conv.lastMessageTime || data.timestamp.toMillis() > conv.lastMessageTime.toMillis())) {
            conv.lastMessage = data.text || '';
            conv.lastMessageTime = data.timestamp;
          }
        }
      };

      // Process all messages
      sentSnapshot.docs.forEach(docSnap => processMessage(docSnap.data()));
      receivedSnapshot.docs.forEach(docSnap => processMessage(docSnap.data()));

      // Convert to array and get participant info
      const userConversations: Conversation[] = [];

      for (const [participantKey, convData] of Array.from(conversationMap.entries())) {
        // Get participant IDs (excluding the selected user)
        const participantIds = Array.from(convData.participantIds).filter(id => id !== userId);

        // Skip conversations with no other participants
        if (participantIds.length === 0) {
          continue;
        }

        // Get participant names - fetch actual participant names from Firestore
        const participantNames: string[] = [];
        let partnerName = 'Unknown';
        for (const participantId of participantIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', participantId));
            const userData = userDoc.data();
            const name = userData?.displayName || userData?.username || 'Unknown';
            participantNames.push(name);
            // Use first participant as the partner
            if (participantNames.length === 1) {
              partnerName = name;
            }
          } catch (err) {
            console.error('Error fetching participant:', participantId, err);
            participantNames.push('Unknown');
          }
        }

        // Use the first conversationId for loading messages
        userConversations.push({
          conversationId: convData.conversationIds[0],
          participantIds: participantIds,
          participantNames,
          partnerId: participantIds[0] || '',
          partnerName: partnerName,
          lastMessage: convData.lastMessage,
          lastMessageTime: convData.lastMessageTime,
          messageCount: convData.messages.length,
          isAdminChat: convData.hasAdminMessage
        });
      }

      // Sort: Admin chats first, then by last message time
      userConversations.sort((a, b) => {
        // Admin chats always come first
        if (a.isAdminChat && !b.isAdminChat) return -1;
        if (!a.isAdminChat && b.isAdminChat) return 1;

        // Within same type, sort by time
        const timeA = a.lastMessageTime?.toMillis() || 0;
        const timeB = b.lastMessageTime?.toMillis() || 0;
        return timeB - timeA;
      });

      setConversations(userConversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadMessages = async (conversationId: string, isAdminConversation: boolean, participantIds: string[]) => {
    if (!selectedUser) return;

    setLoadingMessages(true);
    try {
      const messagesRef = collection(db, 'messages');

      if (isAdminConversation) {
        // For admin conversations: load all admin messages between selected user and admin
        // Query 1: Admin messages sent by selected user
        const q1 = query(
          messagesRef,
          where('senderId', '==', selectedUser.uid),
          where('isAdminMessage', '==', true),
          orderBy('timestamp', 'asc')
        );

        // Query 2: Admin messages received by selected user
        const q2 = query(
          messagesRef,
          where('receiverId', '==', selectedUser.uid),
          where('isAdminMessage', '==', true),
          orderBy('timestamp', 'asc')
        );

        const unsubscribe1 = onSnapshot(q1, (snapshot1) => {
          const unsubscribe2 = onSnapshot(q2, (snapshot2) => {
            const allMessages = new Map<string, Message>();

            // Process sent admin messages
            snapshot1.docs.forEach(docSnap => {
              const data = docSnap.data();
              allMessages.set(docSnap.id, {
                id: docSnap.id,
                conversationId: data.conversationId,
                senderId: data.senderId,
                senderName: data.senderName || selectedUser.displayName || selectedUser.username,
                receiverId: data.receiverId || '',
                text: data.text || '',
                timestamp: data.timestamp,
                isRead: data.isRead || false,
                matchData: data.matchData
              });
            });

            // Process received admin messages
            snapshot2.docs.forEach(docSnap => {
              const data = docSnap.data();
              allMessages.set(docSnap.id, {
                id: docSnap.id,
                conversationId: data.conversationId,
                senderId: data.senderId,
                senderName: data.senderName || 'Admin',
                receiverId: data.receiverId || '',
                text: data.text || '',
                timestamp: data.timestamp,
                isRead: data.isRead || false,
                matchData: data.matchData
              });
            });

            // Convert to array and sort by timestamp
            const messagesData = Array.from(allMessages.values()).sort((a, b) => {
              const timeA = a.timestamp?.toMillis() || 0;
              const timeB = b.timestamp?.toMillis() || 0;
              return timeA - timeB;
            });

            setMessages(messagesData);
            setLoadingMessages(false);
          });
        });
      } else {
        // For normal conversations: load messages by conversationId
        const q = query(
          messagesRef,
          where('conversationId', '==', conversationId),
          orderBy('timestamp', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const messagesData: Message[] = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              conversationId: data.conversationId,
              senderId: data.senderId,
              senderName: data.senderName || 'Unknown',
              receiverId: data.receiverId || '',
              text: data.text || '',
              timestamp: data.timestamp,
              isRead: data.isRead || false,
              matchData: data.matchData
            };
          });

          setMessages(messagesData);
          setLoadingMessages(false);
        });

        return unsubscribe;
      }

      return () => {
        // Cleanup function would need to unsubscribe
      };
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoadingMessages(false);
    }
  };


  const selectConversation = (conv: Conversation) => {
    setSelectedConversationId(conv.conversationId);
    setSelectedConversationIsAdmin(conv.isAdminChat);
    setSelectedConversationPartnerId(conv.partnerId);
    loadMessages(conv.conversationId, conv.isAdminChat, conv.participantIds);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user || !selectedUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const senderName = `${userData?.displayName || userData?.username || 'Admin'} (Admin)`;

      // Create a new conversation ID or use existing one
      let conversationId = selectedConversationId;

      if (!conversationId) {
        // Generate new conversation ID for admin chat
        conversationId = `admin_chat_${user.uid}_${selectedUser.uid}`;
      }

      await addDoc(collection(db, 'messages'), {
        conversationId: conversationId,
        senderId: user.uid,
        senderName: senderName,
        receiverId: selectedUser.uid,
        text: newMessage.trim(),
        timestamp: serverTimestamp(),
        isRead: false,
        isAdminMessage: true
      });

      setNewMessage('');

      // If this was a new conversation, reload conversations and select it
      if (!selectedConversationId) {
        await loadConversations(selectedUser.uid);
        setSelectedConversationId(conversationId);
        setSelectedConversationIsAdmin(true);
        loadMessages(conversationId, true, [user.uid]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return t('common.justNow') || 'Just now';
      if (minutes < 60) return `${minutes}m`;
      if (hours < 24) return `${hours}h`;
      if (days < 7) return `${days}d`;
      return date.toLocaleDateString();
    } catch (err) {
      console.error('Error formatting timestamp:', err);
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#010306]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('sidebar.adminChat') || 'Admin Chat'}
          </h1>
          <p className="text-[#B8B8C3]">
            {t('adminChat.description') || 'View and manage all user conversations'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-[#15191E] rounded-lg border border-[#31383D] p-6">
            <div className="mb-4">
              <input
                type="text"
                placeholder={t('adminChat.searchUsers') || 'Search users...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-[#010306] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-[#00AFF2]"
              />
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00AFF2] mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(u => (
                    <div
                      key={u.uid}
                      onClick={() => setSelectedUser(u)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${selectedUser?.uid === u.uid
                        ? 'bg-[#00AFF2]/20 border border-[#00AFF2]'
                        : 'bg-[#1d232a] hover:bg-[#31383D]'
                        }`}
                    >
                      <p className="text-white font-medium">{u.displayName || u.username}</p>
                      <p className="text-[#8a8f99] text-sm">@{u.username}</p>
                    </div>
                  ))
                ) : searchTerm ? (
                  <p className="text-[#8a8f99] text-center py-4">
                    {t('adminChat.noUsersFound') || 'No users found'}
                  </p>
                ) : (
                  <p className="text-[#8a8f99] text-center py-4">
                    {t('adminChat.searchPrompt') || 'Search for a user to view their conversations'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-[#15191E] rounded-lg border border-[#31383D]">
            {selectedUser ? (
              <div className="flex flex-col h-[600px]">
                <div className="p-4 border-b border-[#31383D]">
                  <h2 className="text-xl font-bold text-white">
                    {selectedUser.displayName || selectedUser.username}
                  </h2>
                  <p className="text-[#8a8f99] text-sm">@{selectedUser.username}</p>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-[#31383D]">
                  <button
                    onClick={() => setActiveTab('messages')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'messages'
                      ? 'text-[#00AFF2] border-b-2 border-[#00AFF2] bg-[#00AFF2]/10'
                      : 'text-[#8a8f99] hover:text-white hover:bg-[#1d232a]'
                      }`}
                  >
                    Üzenetek
                  </button>
                  <button
                    onClick={() => setActiveTab('adminChat')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'adminChat'
                      ? 'text-amber-400 border-b-2 border-amber-500 bg-amber-900/10'
                      : 'text-[#8a8f99] hover:text-white hover:bg-[#1d232a]'
                      }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Admin Chat
                    </span>
                  </button>
                </div>

                {loadingMessages ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00AFF2]"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-8">
                    <svg className="w-16 h-16 text-[#31383D] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-[#8a8f99] text-center">
                      {t('adminChat.noConversations') || 'No conversations found for this user'}
                    </p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-4">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {activeTab === 'messages'
                        ? 'Beszélgetések'
                        : 'Admin Chat'
                      } ({conversations.filter(c => activeTab === 'messages' ? !c.isAdminChat : c.isAdminChat).length})
                    </h3>
                    <div className="space-y-2">
                      {conversations
                        .filter(conv => activeTab === 'messages' ? !conv.isAdminChat : conv.isAdminChat)
                        .map((conv, index) => (
                          <div
                            key={`${conv.conversationId}_${conv.isAdminChat ? 'admin' : 'normal'}_${index}`}
                            onClick={() => selectConversation(conv)}
                            className={`p-4 rounded-lg cursor-pointer transition-all border ${conv.isAdminChat
                              ? 'bg-amber-900/20 border-amber-500/50 hover:bg-amber-900/30 hover:border-amber-500'
                              : 'bg-[#1d232a] border-transparent hover:bg-[#31383D] hover:border-[#00AFF2]/30'
                              }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                {conv.isAdminChat && (
                                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <p className={`font-medium ${conv.isAdminChat ? 'text-amber-400' : 'text-white'}`}>
                                  {conv.partnerName || 'Unknown'}
                                </p>
                              </div>
                              <span className="text-[#8a8f99] text-xs">
                                {formatTimestamp(conv.lastMessageTime)}
                              </span>
                            </div>
                            <p className={`text-sm truncate ${conv.isAdminChat ? 'text-amber-200/70' : 'text-[#8a8f99]'}`}>
                              {conv.lastMessage}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-[#8a8f99] text-xs">
                                {conv.messageCount} {conv.messageCount === 1 ? 'message' : 'messages'}
                              </p>
                              {conv.isAdminChat && (
                                <span className="text-amber-500 text-xs font-medium">Admin Chat</span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Message Sending UI - Only visible in Admin Chat tab */}
                {activeTab === 'adminChat' && (
                  <div className="border-t border-[#31383D] p-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t('adminChat.typeMessage') || 'Írj egy üzenetet...'}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        className="flex-1 px-4 py-2 bg-[#010306] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-amber-500"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        className="px-6 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(245,158,11,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Küldés
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[600px]">
                <p className="text-[#8a8f99]">
                  {t('adminChat.selectUser') || 'Select a user to view their conversations'}
                </p>
              </div>
            )}
          </div>
        </div>

        {messages.length > 0 && (
          <div className="mt-6 bg-[#15191E] rounded-lg border border-[#31383D] p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('adminChat.messageHistory') || 'Message History'}
            </h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`p-4 rounded-lg ${msg.senderId === selectedUser?.uid
                    ? 'bg-[#00AFF2]/20 ml-auto max-w-[70%]'
                    : 'bg-[#1d232a] mr-auto max-w-[70%]'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-white font-medium text-sm">
                      {msg.senderName}
                    </p>
                    <span className="text-[#8a8f99] text-xs ml-2">
                      {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                  <p className="text-[#B8B8C3]">{msg.text}</p>
                  {msg.matchData && (
                    <div className="mt-2 p-2 bg-[#15191E] rounded text-xs">
                      <p className="text-[#00AFF2]">🎯 Match Shared</p>
                      <p className="text-[#8a8f99]">{msg.matchData.gameMode} - {msg.matchData.result}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChat;
